import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import { createModal } from '@codegouvfr/react-dsfr/Modal';
import Tabs from '@codegouvfr/react-dsfr/Tabs';
import { isEqual } from 'lodash-es';
import { planSaveConflicts } from 'maestro-shared/schema/ProgrammingPlan/completedSubPlanSettings';
import { programmingPlanEditionPhase } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlanEditionPhase';
import { canUpdateProgrammingPlanSettings } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlanNationalCoordinator';
import {
  emptyProgrammingPlanSettings,
  pickProgrammingPlanSettings,
  SubstancesSettings,
  withoutOrphanSubstances,
  withSettingsBelowSubstanceKinds
} from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlanSettings';
import {
  ProgrammingLevelSettingsForm,
  ProgrammingSubPlanLevelSettingsForm
} from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlanSettingsForm';
import type { ProgrammingPlanChecked } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlans';
import {
  type ProgrammingSubPlan,
  type ProgrammingSubPlanId,
  SubPlanNumberIssueMessages,
  subPlanNumberIssue
} from 'maestro-shared/schema/ProgrammingPlan/ProgrammingSubPlan';
import {
  sachaContextFieldsIssue,
  unusableSachaContextMessage
} from 'maestro-shared/schema/SachaContext/SachaContext';
import { assertUnreachable } from 'maestro-shared/utils/typescript';
import { checkSchema } from 'maestro-shared/utils/zod';
import {
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import UnsavedChangesGuard, {
  useUnsavedChangesGuard
} from 'src/components/UnsavedChangesGuard/UnsavedChangesGuard';
import { useAuthentication } from 'src/hooks/useAuthentication';
import { useForm } from 'src/hooks/useForm';
import { ApiClientContext } from 'src/services/apiClient';
import { assert, type Equals } from 'tsafe';
import type { z } from 'zod';
import { ProgrammingPlanAnalysisSettings } from '../ProgrammingPlanAnalysisSettings/ProgrammingPlanAnalysisSettings';
import { ProgrammingPlanGlobalSettings } from '../ProgrammingPlanGlobalSettings/ProgrammingPlanGlobalSettings';
import { ProgrammingPlanSamplerFormSettings } from '../ProgrammingPlanSamplerFormSettings/ProgrammingPlanSamplerFormSettings';
import { ProgrammingPlanSampleSettings } from '../ProgrammingPlanSampleSettings/ProgrammingPlanSampleSettings';
import { withSingleSubstanceKindSample } from '../ProgrammingPlanSampleSettings/programmingPlanSamples';
import { ProgrammingSubPlanActionBar } from '../ProgrammingSubPlanActionBar/ProgrammingSubPlanActionBar';

type Props = {
  programmingPlan: ProgrammingPlanChecked;
  subPlan: ProgrammingSubPlan | undefined;
  titleDraft: string | undefined;
  onResetTitle: () => void;
};

const emptySettings: ProgrammingLevelSettingsForm = {
  ...emptyProgrammingPlanSettings(false),
  settingsCompleted: false,
  nationalCoordinators: null,
  technicalInstruction: null,
  subPlanNumber: null,
  sachaContextSigle: null,
  fields: []
};

const settingsTabs = [
  { tabId: 'global', label: 'Paramétrage global' },
  { tabId: 'sampler-form', label: 'Formulaire préleveur' },
  { tabId: 'samples', label: 'Échantillons' },
  { tabId: 'analyses', label: 'Analyses' }
] as const satisfies { tabId: string; label: string }[];

type SettingsTabId = (typeof settingsTabs)[number]['tabId'];

type SettingsFieldKey = Exclude<
  keyof ProgrammingLevelSettingsForm,
  `${string}Managed` | 'settingsCompleted'
>;

const completionModal = createModal({
  id: 'programming-plan-settings-completion-modal',
  isOpenedByDefault: false
});

const tabIdBySettingsKey: Record<SettingsFieldKey, SettingsTabId> = {
  stages: 'global',
  substanceKinds: 'global',
  samples: 'samples',
  sampleProcedure: 'samples',
  matrices: 'global',
  context: 'global',
  programmingInstruction: 'global',
  notes: 'global',
  monoSubstances: 'analyses',
  multiSubstances: 'analyses',
  nationalCoordinators: 'global',
  technicalInstruction: 'global',
  subPlanNumber: 'global',
  sachaContextSigle: 'global',
  fields: 'sampler-form'
};

const planSaveConflictMessage = ({
  settingKey,
  reason,
  subPlanNumbers
}: ReturnType<typeof planSaveConflicts>[number]) =>
  reason === 'missing'
    ? `Ne peut pas être vide : les sous-plans terminés ${subPlanNumbers.join(', ')} l’utilisent.`
    : SubstancesSettings.some((setting) => setting.settingKey === settingKey)
      ? `Des substances des sous-plans terminés ${subPlanNumbers.join(', ')} seraient à la fois en mono-résidu et en multi-résidus.`
      : `Les échantillons des sous-plans terminés ${subPlanNumbers.join(', ')} ne correspondraient plus à leurs analytes.`;

export const ProgrammingPlanSettingsTabs = ({
  programmingPlan,
  subPlan,
  titleDraft,
  onResetTitle,
  ..._rest
}: Props) => {
  assert<Equals<keyof typeof _rest, never>>();

  const apiClient = useContext(ApiClientContext);

  const programmingPlanId = programmingPlan.id;

  const { data: programmingPlans = [] } =
    apiClient.useFindProgrammingPlansQuery({});
  const { data: planSettings, isFetching: isFetchingPlanSettings } =
    apiClient.useFindProgrammingPlanSettingsQuery(
      { programmingPlanId },
      { skip: !!subPlan }
    );
  const { data: subPlanSettings, isFetching: isFetchingSubPlanSettings } =
    apiClient.useFindProgrammingSubPlanSettingsQuery(
      {
        programmingPlanId,
        programmingSubPlanId: subPlan?.id as ProgrammingSubPlanId
      },
      { skip: !subPlan }
    );

  const { data: sachaContexts } = apiClient.useFindSachaContextsQuery(
    undefined,
    { skip: !subPlan }
  );
  const { data: fieldConfigs } = apiClient.useFindAllFieldConfigsQuery(
    undefined,
    { skip: !subPlan }
  );
  const { data: sachaCommemoratifs } = apiClient.useGetSachaCommemoratifsQuery(
    undefined,
    { skip: !subPlan }
  );

  const [updateProgrammingPlanSettings, updatePlanSettingsCall] =
    apiClient.useUpdateProgrammingPlanSettingsMutation();
  const [updateProgrammingSubPlanSettings, updateSubPlanSettingsCall] =
    apiClient.useUpdateProgrammingSubPlanSettingsMutation();
  const [createDocumentUpload, createDocumentUploadCall] =
    apiClient.useCreateDocumentUploadMutation();

  const saving =
    createDocumentUploadCall.isLoading ||
    updatePlanSettingsCall.isLoading ||
    updateSubPlanSettingsCall.isLoading ||
    isFetchingPlanSettings ||
    isFetchingSubPlanSettings;

  const { user, account } = useAuthentication();

  const readOnly =
    !user ||
    !account ||
    !canUpdateProgrammingPlanSettings(programmingPlan, user, account.roles);

  const editionPhase = programmingPlanEditionPhase(programmingPlan);

  const settings: ProgrammingLevelSettingsForm | undefined = useMemo(
    () =>
      subPlan
        ? subPlanSettings && {
            ...subPlanSettings,
            nationalCoordinators: null,
            technicalInstruction: null,
            subPlanNumber: subPlan.subPlanNumber,
            sachaContextSigle: subPlan.sachaContextSigle
          }
        : planSettings && {
            ...planSettings,
            subPlanNumber: null,
            sachaContextSigle: null,
            fields: planSettings.fields.map((field) => ({
              ...field,
              inheritance: 'Own' as const,
              managedAtPlanLevel: false
            }))
          },
    [subPlan, subPlanSettings, planSettings]
  );

  const [draft, setDraft] = useState<ProgrammingLevelSettingsForm>();
  const [technicalInstructionFile, setTechnicalInstructionFile] =
    useState<File>();

  const hasTitleChange =
    titleDraft !== undefined && titleDraft !== programmingPlan.title;

  const resetDraft = () => {
    setDraft(settings);
    setTechnicalInstructionFile(undefined);
    createDocumentUploadCall.reset();
    onResetTitle();
  };

  useEffect(resetDraft, [settings]);

  const hasChanges =
    !!draft &&
    (!isEqual(draft, settings) ||
      technicalInstructionFile !== undefined ||
      hasTitleChange);

  const unsavedChangesGuard = useUnsavedChangesGuard({
    when: hasChanges,
    onDiscard: resetDraft
  });

  const [selectedTabId, setSelectedTabId] = useState<SettingsTabId>('global');

  const formSchema = useMemo(
    () =>
      subPlan
        ? checkSchema(ProgrammingSubPlanLevelSettingsForm, (ctx) => {
            const issue = subPlanNumberIssue(
              subPlan.subPlanNumber,
              ctx.value.subPlanNumber ?? '',
              programmingPlans
                .filter(({ year }) => year === programmingPlan.year)
                .flatMap(({ subPlans }) => subPlans.map((_) => _.subPlanNumber))
            );
            if (issue) {
              ctx.issues.push({
                input: ctx.value,
                code: 'custom',
                message: SubPlanNumberIssueMessages[issue],
                path: ['subPlanNumber']
              });
            }
            const { sachaContextSigle } = ctx.value;
            if (!ctx.value.settingsCompleted || !sachaContextSigle) {
              return;
            }
            const sachaContext = sachaContexts?.find(
              ({ sigle }) => sigle === sachaContextSigle
            );
            if (sachaContexts && !sachaContext) {
              ctx.issues.push({
                input: ctx.value,
                code: 'custom',
                message: unusableSachaContextMessage(sachaContextSigle),
                path: ['sachaContextSigle']
              });
            }
            const fieldsIssue =
              sachaContext &&
              sachaContextFieldsIssue(
                sachaContext,
                ctx.value.fields,
                fieldConfigs ?? [],
                sachaCommemoratifs ?? {}
              );
            if (fieldsIssue) {
              ctx.issues.push({
                input: ctx.value,
                code: 'custom',
                message: fieldsIssue,
                path: ['fields']
              });
            }
          })
        : checkSchema(ProgrammingLevelSettingsForm, (ctx) => {
            for (const conflict of planSaveConflicts(
              programmingPlan.subPlans,
              programmingPlan,
              ctx.value
            )) {
              ctx.issues.push({
                input: ctx.value,
                code: 'custom',
                message: planSaveConflictMessage(conflict),
                path: [conflict.settingKey]
              });
            }
          }),
    [
      subPlan,
      programmingPlan,
      programmingPlans,
      sachaContexts,
      fieldConfigs,
      sachaCommemoratifs
    ]
  );

  const [validation, setValidation] = useState<{
    settingsCompleted: boolean;
  }>();

  const form = useForm(formSchema, {
    ...(draft ?? emptySettings),
    settingsCompleted: validation?.settingsCompleted ?? true
  });

  const uploadTechnicalInstruction = async (
    technicalInstruction: ProgrammingLevelSettingsForm['technicalInstruction']
  ) => {
    if (!technicalInstructionFile) {
      return technicalInstruction;
    }
    const { documentId } = await createDocumentUpload(
      technicalInstructionFile
    ).unwrap();
    return { id: documentId, filename: technicalInstructionFile.name };
  };

  const save = async (
    draft: ProgrammingLevelSettingsForm,
    settingsCompleted: boolean
  ): Promise<boolean> => {
    if (saving) {
      return false;
    }
    try {
      if (subPlan) {
        await updateProgrammingSubPlanSettings({
          programmingPlanId,
          programmingSubPlanId: subPlan.id,
          subPlanNumber:
            draft.subPlanNumber !== subPlan.subPlanNumber
              ? (draft.subPlanNumber ?? undefined)
              : undefined,
          sachaContextSigle:
            draft.sachaContextSigle !== subPlan.sachaContextSigle
              ? draft.sachaContextSigle
              : undefined,
          ...pickProgrammingPlanSettings(withoutOrphanSubstances(draft)),
          fields: draft.fields,
          settingsCompleted
        }).unwrap();
      } else {
        await updateProgrammingPlanSettings({
          programmingPlanId,
          ...pickProgrammingPlanSettings(withoutOrphanSubstances(draft)),
          title: hasTitleChange ? titleDraft : undefined,
          nationalCoordinators: draft.nationalCoordinators ?? [],
          technicalInstruction: await uploadTechnicalInstruction(
            draft.technicalInstruction
          ),
          settingsCompleted,
          fields: draft.fields.map(({ fieldId, required, optionIds }) => ({
            fieldId,
            required,
            optionIds
          }))
        }).unwrap();
      }
      return true;
    } catch (_err) {
      return false;
    }
  };

  const complete = async (draft: ProgrammingLevelSettingsForm) => {
    if (await save(draft, true)) {
      form.reset();
    }
  };

  const selectTabInError = ({ issues }: z.ZodError) => {
    const tabIdsInError = Object.entries(tabIdBySettingsKey)
      .filter(([settingsKey]) =>
        issues.some(({ path }) => path[0] === settingsKey)
      )
      .map(([, tabId]) => tabId);

    setSelectedTabId(
      settingsTabs.find(({ tabId }) => tabIdsInError.includes(tabId))?.tabId ??
        selectedTabId
    );
  };

  useEffect(() => {
    if (!validation || !draft) {
      return;
    }
    form.validate(async () => {
      if (!validation.settingsCompleted) {
        await save(draft, false);
      } else if (draft.settingsCompleted) {
        await complete(draft);
      } else {
        completionModal.open();
      }
    }, selectTabInError);
  }, [validation]);

  if (!draft) {
    return null;
  }

  const changeDraft = (changedDraft: ProgrammingLevelSettingsForm) => {
    const cascadedDraft = withSettingsBelowSubstanceKinds(
      changedDraft,
      subPlan ? programmingPlan : undefined
    );
    setDraft(
      withoutOrphanSubstances(
        isEqual(changedDraft.substanceKinds, draft.substanceKinds)
          ? cascadedDraft
          : withSingleSubstanceKindSample(cascadedDraft)
      )
    );
  };

  const tabContent = (tabId: SettingsTabId): ReactNode => {
    switch (tabId) {
      case 'global':
        return (
          <ProgrammingPlanGlobalSettings
            settings={draft}
            planSettings={subPlan ? programmingPlan : undefined}
            editionPhase={editionPhase}
            technicalInstructionFile={technicalInstructionFile}
            inputForm={form}
            onChange={changeDraft}
            onTechnicalInstructionFileChange={setTechnicalInstructionFile}
          />
        );
      case 'sampler-form':
        return (
          <ProgrammingPlanSamplerFormSettings
            parentPlanId={subPlan ? programmingPlanId : undefined}
            fields={draft.fields}
            errorMessage={form.message('fields')}
            editionPhase={editionPhase}
            onChange={(fields) => setDraft({ ...draft, fields })}
          />
        );
      case 'samples':
        return (
          <ProgrammingPlanSampleSettings
            settings={draft}
            planSettings={subPlan ? programmingPlan : undefined}
            editionPhase={editionPhase}
            inputForm={form}
            onChange={changeDraft}
          />
        );
      case 'analyses':
        return (
          <ProgrammingPlanAnalysisSettings
            settings={draft}
            planSettings={subPlan ? programmingPlan : undefined}
            editionPhase={editionPhase}
            inputForm={form}
            onChange={changeDraft}
          />
        );
      default:
        assertUnreachable(tabId);
    }
  };

  return (
    <>
      <Tabs
        className={cx('fr-mt-3w')}
        tabs={settingsTabs.map(({ tabId, label }) => ({ tabId, label }))}
        selectedTabId={selectedTabId}
        onTabChange={(tabId) => setSelectedTabId(tabId as SettingsTabId)}
      >
        <div inert={readOnly}>{tabContent(selectedTabId)}</div>
      </Tabs>
      {!readOnly && (hasChanges || !draft.settingsCompleted) && (
        <>
          <ProgrammingSubPlanActionBar
            completed={draft.settingsCompleted}
            hasChanges={hasChanges}
            saving={saving}
            saveCall={
              subPlan
                ? updateSubPlanSettingsCall
                : createDocumentUploadCall.isError
                  ? createDocumentUploadCall
                  : updatePlanSettingsCall
            }
            onReset={resetDraft}
            onSaveDraft={() => setValidation({ settingsCompleted: false })}
            onComplete={() => setValidation({ settingsCompleted: true })}
          />
          <ConfirmationModal
            modal={completionModal}
            title="Terminer le paramétrage"
            confirmLabel="Terminer"
            onConfirm={() => complete(draft)}
            closeOnConfirm
          >
            Vous vous apprêtez à terminer le paramétrage{' '}
            {subPlan ? 'de ce sous-plan' : 'de ce plan'}. Il restera modifiable,
            mais ne pourra plus revenir à l’état de brouillon.
          </ConfirmationModal>
        </>
      )}
      <UnsavedChangesGuard guard={unsavedChangesGuard} />
    </>
  );
};
